import type {
  MemoryFeastAction,
  MemoryFeastState,
  MemoryFeastViewerState,
} from "@cloudnest/memory-feast-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/memory-feast-engine";
import { memoryFeastEventToAction } from "./event-adapter";
import type { MemoryFeastReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 기억의 만찬 (memory-feast) replay simulation. `init` seeds canonical
 * state; every other event is reconstructed via the event adapter and folded
 * through the engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Hidden information: each snapshot carries both the canonical `state` (for the
 * post-game recap toggle) and a `viewer` projection where covered bowl counts
 * are masked. The replay screen renders `viewer` by default so private values
 * never reach the DOM during step-through.
 */
export interface MemoryFeastReplaySnapshot {
  readonly index: number;
  readonly event: MemoryFeastReplayFixtureEvent;
  readonly action: MemoryFeastAction | null;
  readonly state: MemoryFeastState;
  readonly viewer: MemoryFeastViewerState;
}

function requireState(
  state: MemoryFeastState | null,
  eventType: string,
): MemoryFeastState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function snapshot(
  index: number,
  event: MemoryFeastReplayFixtureEvent,
  action: MemoryFeastAction | null,
  state: MemoryFeastState,
): MemoryFeastReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    viewer: filterStateForViewer(state, null),
  };
}

export function buildMemoryFeastReplaySnapshots(
  events: readonly MemoryFeastReplayFixtureEvent[],
): readonly MemoryFeastReplaySnapshot[] {
  const snapshots: MemoryFeastReplaySnapshot[] = [];
  let state: MemoryFeastState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as { state: MemoryFeastState }).state;
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = memoryFeastEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty memory-feast event log");
  }
  return snapshots;
}
